import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { clearTokens, loadTokens, saveTokens } from "../api/secureTokens";
import { env } from "../constants/env";

// Rôles backend (claim Keycloak realm_access.roles). Source de vérité = openapi.json.
export type AppRole = "CLIENT" | "AGENT" | "SUPERVISOR" | "ADMIN";

type SessionState = {
  // Tokens Keycloak OIDC — chargés depuis le stockage de session au boot via
  // onRehydrateStorage. JAMAIS persistés en localStorage : ils vivent dans
  // sessionStorage (cf. api/secureTokens.ts), qui meurt avec l'onglet.
  accessToken?: string;
  refreshToken?: string;
  tokenExpiry?: number; // ms timestamp — expiry access token
  refreshTokenExpiry?: number; // ms timestamp — expiry refresh token (0 = offline session)
  idToken?: string;

  // Identité extraite du JWT (non sensible — persistée en localStorage)
  userId?: string; // claim "sub"
  displayName?: string; // claim "name" (nettoyé) — affiché dans le header/profil
  agentId?: string;
  role: AppRole;

  // Navigation
  // hasCompletedOnboarding : le 1ᵉʳ parcours d'auth complet (login Keycloak /
  // ProfilMenage) est terminé.
  //
  // Le pendant `hasSeenOnboarding` a disparu avec le carrousel d'introduction :
  // il ne servait qu'à ne montrer les slides qu'au premier lancement, une
  // notion propre à une app installée.
  hasCompletedOnboarding: boolean;

  // Indique que les tokens ont fini d'être lus depuis sessionStorage au boot.
  // Le routeur attend ce flag avant de router pour éviter le flash
  // écran d'auth → écran client quand la session est en réalité valide.
  _tokensHydrated: boolean;

  // ─── Dev tools (non persistés — reset à chaque lancement) ───
  devBypassAuth: boolean;

  // ─── Actions ─────────────────────────────────────────────────
  // idToken est optionnel : le nouveau contrat backend (KeycloakTokenResponse)
  // ne le documente plus. On le stocke s'il est présent (champ non-documenté
  // mais parfois renvoyé par Keycloak), undefined sinon.
  setSession: (params: {
    accessToken: string;
    refreshToken: string;
    idToken?: string;
    tokenExpiry: number;
    refreshTokenExpiry: number; // 0 si offline session Keycloak
    userId: string;
    displayName?: string;
    role: AppRole;
  }) => void;

  updateTokens: (
    accessToken: string,
    refreshToken: string,
    tokenExpiry: number,
    refreshTokenExpiry: number,
  ) => void;

  completeOnboarding: () => void;

  // Met à jour l'agentId courant après résolution via /agents
  setAgentId: (id: string) => void;

  // Efface uniquement les tokens (session expirée, refresh échoué…)
  clearSession: () => void;

  // Dev actions
  setDevBypassAuth: (value: boolean) => void;
  resetForOnboardingTest: () => void;
};

// Wrapper qui synchronise le state Zustand avec le stockage de session : à
// chaque write de token, on déclenche un save. Les `await` ne sont pas
// nécessaires (fire-and-forget) — sessionStorage est synchrone sous le capot.
function persistTokensFromState(
  state: Pick<SessionState, "accessToken" | "refreshToken" | "idToken">,
) {
  void saveTokens({
    accessToken: state.accessToken,
    refreshToken: state.refreshToken,
    idToken: state.idToken,
  });
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      role: "CLIENT",
      hasCompletedOnboarding: false,
      _tokensHydrated: false,
      // Bypass auth est strictement réservé au dev local. En build de
      // production (env.isDev = false), le flag reste toujours false même si
      // setDevBypassAuth(true) est appelé.
      devBypassAuth: false,

      setSession: ({
        accessToken,
        refreshToken,
        idToken,
        tokenExpiry,
        refreshTokenExpiry,
        userId,
        displayName,
        role,
      }) => {
        set({
          accessToken,
          refreshToken,
          idToken,
          tokenExpiry,
          refreshTokenExpiry,
          userId,
          displayName,
          role,
          _tokensHydrated: true,
        });
        persistTokensFromState({ accessToken, refreshToken, idToken });
      },

      updateTokens: (accessToken, refreshToken, tokenExpiry, refreshTokenExpiry) => {
        // Garde ANTI-RÉSURRECTION : un refresh proactif (token watcher) ou le
        // retry de l'intercepteur 401 peut être IN-FLIGHT au moment d'un logout.
        // S'il résout APRÈS clearSession(), il ne doit PAS recréer une session.
        // clearSession() vide accessToken ET refreshToken → on détecte cet état
        // de déconnexion ici et on ignore la mise à jour.
        const current = get();
        if (!current.accessToken && !current.refreshToken) {
          if (env.isDev) {
            console.log("[session] updateTokens ignoré — session déjà purgée (logout)");
          }
          return;
        }
        set({
          accessToken,
          refreshToken,
          tokenExpiry,
          refreshTokenExpiry,
          idToken: current.idToken,
        });
        persistTokensFromState({ accessToken, refreshToken, idToken: undefined });
      },

      completeOnboarding: () => set({ hasCompletedOnboarding: true }),

      setAgentId: (id) => set({ agentId: id }),

      clearSession: () => {
        set({
          accessToken: undefined,
          refreshToken: undefined,
          idToken: undefined,
          tokenExpiry: undefined,
          refreshTokenExpiry: undefined,
          userId: undefined,
          displayName: undefined,
          agentId: undefined,
        });
        void clearTokens();
      },

      // No-op en production : impossible d'activer le bypass via tampering du store.
      setDevBypassAuth: (value) => set({ devBypassAuth: env.isDev ? value : false }),

      resetForOnboardingTest: () => {
        set({
          devBypassAuth: false,
          accessToken: undefined,
          refreshToken: undefined,
          idToken: undefined,
          tokenExpiry: undefined,
          refreshTokenExpiry: undefined,
          userId: undefined,
          displayName: undefined,
          hasCompletedOnboarding: false,
        });
        void clearTokens();
      },
    }),
    {
      name: "sos-main-session",
      storage: createJSONStorage(() => localStorage),
      // ⚠️ Les tokens sont volontairement exclus : ils vivent dans
      // sessionStorage. Voir loadTokens() / onRehydrateStorage ci-dessous.
      partialize: (state) => ({
        tokenExpiry: state.tokenExpiry,
        refreshTokenExpiry: state.refreshTokenExpiry,
        userId: state.userId,
        displayName: state.displayName,
        agentId: state.agentId,
        role: state.role,
        hasCompletedOnboarding: state.hasCompletedOnboarding,
      }),
      // Hydratation des tokens depuis sessionStorage après que Zustand ait
      // restauré le payload localStorage. Si le stockage est indisponible
      // (navigation privée stricte, cookies bloqués…) on bascule simplement
      // sans tokens → le routeur envoie sur l'écran d'auth.
      //
      // ⚠️ Écart de comportement assumé vs natif : sessionStorage étant vidé à
      // la fermeture de l'onglet, l'identité persistée en localStorage (userId,
      // displayName, role) survit alors qu'il n'y a plus de token. C'est
      // cohérent — le routeur ne route que sur la présence d'accessToken — et
      // ça évite de reperdre le profil affiché à chaque nouvel onglet.
      onRehydrateStorage: () => async () => {
        try {
          const tokens = await loadTokens();
          useSessionStore.setState({
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            idToken: tokens.idToken,
            _tokensHydrated: true,
          });
        } catch {
          useSessionStore.setState({ _tokensHydrated: true });
        }
      },
    },
  ),
);
